// Shared cook-time session state across CookMode + CookComplete.
//
// The problem this solves: CookMode and CookComplete each kept their
// own "user override" state (CookMode's pantrySwaps / swappedToShopping,
// CookComplete's usedItems + extraRemovals). User-visible consequence:
// swap Mozzarella → Parmesan on the cook-prep screen, then hit DONE →
// the "What did you use?" screen shows Mozzarella again and deducts
// from the wrong row. Three parallel representations of the same
// "what has the user changed this cook?" question, none of them
// talking to each other.
//
// This hook centralizes ONE shape both screens read and write:
//
//   session = {
//     overrides: {
//       [recipeIngIdx]: {
//         pantryItemId?        — the pantry row the user bound to this slot
//         promotedToShopping?  — user tapped + SHOP on this row
//         skipped?             — user tapped ✕ / dropped this row
//         usedAmount?          — final commit amount (CookComplete only)
//         usedUnit?
//       }
//     },
//     extras: [
//       { id, name, amount, unit, pantryItemId, ingredientId, source }
//     ]   — user-added ingredients not in recipe.ingredients
//   }
//
// Design principle (documented at recipePairing.js:163): recipes store
// INTENT ("butter"), not POINTERS ("row id 42ba…"). The session is
// cook-time STATE that layers ON TOP of the persisted recipe — if the
// user cooks this recipe again in April, the session resets and
// pairing re-derives against April's pantry. This hook is session-
// scoped on purpose; nothing here writes to user_recipes.
//
// AIRecipe doesn't use this hook — its tweak-phase edits DO persist
// (via buildLockedIngredients → final recipe's pantryItemId stamping),
// so its local state is the right model for that phase. CookMode +
// CookComplete are the post-persist screens where overrides are
// ephemeral; they're what this hook is for.

import { useCallback, useEffect, useRef, useState } from "react";

// Crash / app-kill resilience. Mid-cook overrides (counter-list
// check-offs, pantry swaps, unit picks, extras) are too expensive to
// lose to an iOS background-kill — the banner resume restores the
// recipe + step index from cook_sessions, but the override state was
// pure React memory and came back empty. Persist it to localStorage
// keyed by recipe slug, debounced, with a TTL so a session abandoned
// without cleanup can't resurrect days later. localStorage (not
// sessionStorage) on purpose: WKWebView drops sessionStorage on app
// kill, which is exactly the failure being defended against.
const SESSION_TTL_MS = 12 * 60 * 60 * 1000;
const PERSIST_DEBOUNCE_MS = 400;
const EMPTY_SESSION = { overrides: {}, extras: [] };

function storageKeyFor(persistKey) {
  return persistKey ? `mise_cook_session:${persistKey}` : null;
}

function loadPersisted(storageKey) {
  if (!storageKey || typeof localStorage === "undefined") return null;
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    if (!Number.isFinite(parsed.savedAt) || Date.now() - parsed.savedAt > SESSION_TTL_MS) {
      localStorage.removeItem(storageKey);
      return null;
    }
    const s = parsed.session;
    if (!s || typeof s !== "object") return null;
    return {
      overrides: (s.overrides && typeof s.overrides === "object") ? s.overrides : {},
      extras: Array.isArray(s.extras) ? s.extras : [],
    };
  } catch {
    return null;
  }
}

function clearPersisted(storageKey) {
  if (!storageKey || typeof localStorage === "undefined") return;
  try { localStorage.removeItem(storageKey); } catch {}
}

// `persistKey` (typically recipe.slug) opts the session into
// localStorage persistence. Omit it for callers that genuinely want
// memory-only state. When the key CHANGES mid-mount (parent swapped
// the recipe prop without remounting), the hook rehydrates-or-resets
// for the new key — callers no longer need their own reset effect,
// which used to fire on first mount and would wipe the hydrated
// state before the cook ever saw it.
export function useCookSession(persistKey = null) {
  const storageKey = storageKeyFor(persistKey);
  const [session, setSession] = useState(() =>
    loadPersisted(storageKey) || EMPTY_SESSION);

  // Rehydrate-or-reset when the recipe (persist key) changes without
  // a remount. Skipped on initial mount so the useState hydration
  // above survives.
  const keyRef = useRef(storageKey);
  useEffect(() => {
    if (keyRef.current === storageKey) return;
    keyRef.current = storageKey;
    setSession(loadPersisted(storageKey) || EMPTY_SESSION);
  }, [storageKey]);

  // Debounced persistence — slider drags and rapid check-offs batch
  // into one write. An empty session removes the key instead of
  // storing a husk.
  const writeTimer = useRef(null);
  useEffect(() => {
    if (!storageKey || typeof localStorage === "undefined") return undefined;
    if (writeTimer.current) clearTimeout(writeTimer.current);
    writeTimer.current = setTimeout(() => {
      try {
        const empty = Object.keys(session.overrides).length === 0
          && session.extras.length === 0;
        if (empty) {
          localStorage.removeItem(storageKey);
        } else {
          localStorage.setItem(storageKey, JSON.stringify({ savedAt: Date.now(), session }));
        }
      } catch {}
    }, PERSIST_DEBOUNCE_MS);
    return () => { if (writeTimer.current) clearTimeout(writeTimer.current); };
  }, [session, storageKey]);

  // Merge a patch into the override for a given recipe ingredient
  // index. Partial writes — e.g. { pantryItemId: "row-42" } keeps any
  // existing { usedAmount } on the same idx.
  const setOverride = useCallback((idx, patch) => {
    setSession(prev => ({
      ...prev,
      overrides: {
        ...prev.overrides,
        [idx]: { ...(prev.overrides[idx] || {}), ...patch },
      },
    }));
  }, []);

  // Remove specific fields OR the entire override for an idx.
  //   clearOverride(3)                    — wipe overrides[3] entirely
  //   clearOverride(3, ["pantryItemId"])  — unset just that field
  const clearOverride = useCallback((idx, fields) => {
    setSession(prev => {
      if (!fields) {
        const next = { ...prev.overrides };
        delete next[idx];
        return { ...prev, overrides: next };
      }
      const cur = prev.overrides[idx];
      if (!cur) return prev;
      const nextCur = { ...cur };
      for (const f of fields) delete nextCur[f];
      return {
        ...prev,
        overrides: Object.keys(nextCur).length === 0
          ? (() => { const { [idx]: _, ...rest } = prev.overrides; return rest; })()
          : { ...prev.overrides, [idx]: nextCur },
      };
    });
  }, []);

  const addExtra = useCallback((row) => {
    setSession(prev => ({ ...prev, extras: [...prev.extras, row] }));
  }, []);

  const removeExtra = useCallback((id) => {
    setSession(prev => ({ ...prev, extras: prev.extras.filter(e => e.id !== id) }));
  }, []);

  // Hard reset — wipes state AND the persisted copy. Call on cook
  // completion / abandon so a finished cook can't resurrect its
  // overrides on the next cook of the same recipe. (Recipe-change
  // resets are handled inside the hook via persistKey now.)
  const resetSession = useCallback(() => {
    setSession(EMPTY_SESSION);
    clearPersisted(storageKey);
  }, [storageKey]);

  return { session, setOverride, clearOverride, addExtra, removeExtra, resetSession };
}

// Read helpers — callers can use these OR drill into session.overrides
// directly. These exist so unrelated components (describePairing hooks,
// pair resolvers) can check state without pulling the full hook.

export function getOverridePantryItemId(session, idx) {
  return session?.overrides?.[idx]?.pantryItemId || null;
}

export function isPromotedToShopping(session, idx) {
  return !!session?.overrides?.[idx]?.promotedToShopping;
}

export function isSkipped(session, idx) {
  return !!session?.overrides?.[idx]?.skipped;
}

// "Added to the bowl" — the cook checked this ingredient off during a
// mise-en-place / combine step. Ephemeral like every other override;
// toggle via setOverride(idx, { addedToBowl: !isAddedToBowl(session, idx) }).
export function isAddedToBowl(session, idx) {
  return !!session?.overrides?.[idx]?.addedToBowl;
}

export function getOverrideAmount(session, idx) {
  const o = session?.overrides?.[idx];
  if (!o) return null;
  return {
    amount: o.usedAmount ?? null,
    unit:   o.usedUnit   ?? null,
  };
}
